import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Droplets,
  Calendar as CalIcon,
  MessageCircle,
  Gauge,
  MapPin,
  Bell,
  Building2,
  Waves,
  Users,
  BarChart3,
  Factory,
  Home,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Landing() {
  const { t } = useI18n();
  const [track, setTrack] = useState("muni");

  const hero = {
    muni: { title: t("hero_muni_title"), sub: t("hero_muni_sub"), Icon: Building2 },
    util: { title: t("hero_util_title"), sub: t("hero_util_sub"), Icon: Waves },
    consumer: { title: t("hero_consumer_title"), sub: t("hero_consumer_sub"), Icon: Home },
  };
  const active = hero[track];

  const tabs = [
    { id: "muni", label: t("track_muni_title"), Icon: Building2 },
    { id: "util", label: t("track_util_title"), Icon: Waves },
    { id: "consumer", label: t("track_consumer_title"), Icon: Home },
  ];

  const tracks = [
    {
      icon: Building2,
      title: t("track_muni_title"),
      desc: t("track_muni_desc"),
      features: [
        { icon: Gauge, title: t("daily_quota"), desc: t("quota_usage") },
        { icon: CalIcon, title: t("bookings"), desc: t("all_bookings") },
        { icon: MessageCircle, title: t("messages"), desc: t("contact") },
      ],
      accent: "border-primary/40",
      testId: "track-muni",
    },
    {
      icon: Waves,
      title: t("track_util_title"),
      desc: t("track_util_desc"),
      features: [
        { icon: BarChart3, title: t("demand_curve"), desc: t("forecast") },
        { icon: Users, title: t("all_municipalities"), desc: t("scope") },
        { icon: Factory, title: t("large_consumer"), desc: t("request_water") },
      ],
      accent: "border-accent/40",
      testId: "track-util",
    },
    {
      icon: Home,
      title: t("track_consumer_title"),
      desc: t("track_consumer_desc"),
      features: [
        { icon: Droplets, title: t("book_filling"), desc: t("calc_hint") },
        { icon: Bell, title: t("reminders"), desc: t("water_care") },
        { icon: MapPin, title: t("location"), desc: t("view_map") },
      ],
      accent: "border-border",
      testId: "track-consumer",
    },
  ];

  return (
    <div className="relative water-accent">
      <section className="grid gap-10 py-10 md:grid-cols-2 md:py-16">
        <div className="flex flex-col justify-center">
          <div className="label-caps mb-4">{t("tagline")}</div>

          <div
            className="mb-6 inline-flex w-fit rounded-md border border-border bg-card p-1"
            role="tablist"
            data-testid="track-tabs"
          >
            {tabs.map((tb) => {
              const isActive = tb.id === track;
              return (
                <button
                  key={tb.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTrack(tb.id)}
                  data-testid={`track-tab-${tb.id}`}
                  className={
                    "flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm font-semibold transition-colors " +
                    (isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  <tb.Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tb.label}</span>
                </button>
              );
            })}
          </div>

          <h1
            key={track}
            className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="hero-title"
          >
            {active.title}
          </h1>
          <p
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
            data-testid="hero-sub"
          >
            {active.sub}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="gap-2" data-testid="cta-get-started">
                <Droplets className="h-4 w-4" />
                {t("get_started")}
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" data-testid="cta-login">
                {t("login")}
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg border border-border bg-card">
          <img
            src="/images/hero-pool.jpg"
            alt="Garden pool"
            className="h-full w-full object-cover"
            style={{ minHeight: 380 }}
          />
          <div className="absolute inset-x-0 bottom-0 border-t border-border bg-background/95 p-4">
            <div className="label-caps">Live · {t("daily_quota")}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-mono-jb text-3xl font-semibold text-primary">72</span>
              <span className="text-sm text-muted-foreground">/ 100 m³</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {t("features")}
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {tracks.map((tr, i) => (
            <Card key={i} className={`card-hover ${tr.accent}`} data-testid={tr.testId}>
              <CardContent className="flex h-full flex-col gap-5 p-6">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-md bg-primary/10 text-primary">
                    <tr.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-outfit text-lg font-semibold tracking-tight">
                      {tr.title}
                    </div>
                    <div className="text-sm text-muted-foreground">{tr.desc}</div>
                  </div>
                </div>
                <ul className="space-y-3 border-t border-border pt-4">
                  {tr.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <div className="mt-0.5 grid h-8 w-8 place-items-center rounded-md border border-border text-primary">
                        <f.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{f.title}</div>
                        {f.desc && (
                          <div className="text-xs text-muted-foreground">{f.desc}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <WaitlistSection />
    </div>
  );
}

function WaitlistSection() {
  const { t } = useI18n();
  const [municipality, setMunicipality] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waitlist/municipality", { municipality, name, email, note });
      setSent(true);
      toast.success(t("waitlist_sent"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("waitlist_error"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-12" data-testid="waitlist-section">
      <Card className="border-primary/40 bg-primary/[0.03]">
        <CardContent className="grid gap-6 p-6 md:grid-cols-5 md:p-8">
          <div className="md:col-span-2">
            <div className="label-caps mb-2 text-primary">
              <Building2 className="mr-1 inline h-3.5 w-3.5" /> Water Planner Pilot
            </div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t("waitlist_title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {t("waitlist_sub")}
            </p>
          </div>
          <div className="md:col-span-3">
            {sent ? (
              <div
                className="grid h-full place-items-center rounded-md border border-primary/40 bg-background p-8 text-center"
                data-testid="waitlist-success"
              >
                <div>
                  <div className="mb-2 text-3xl">✓</div>
                  <p className="text-sm font-medium">{t("waitlist_sent")}</p>
                </div>
              </div>
            ) : (
              <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit} data-testid="waitlist-form">
                <div>
                  <Label htmlFor="wl-muni">{t("waitlist_muni_field")}</Label>
                  <Input
                    id="wl-muni"
                    required
                    value={municipality}
                    onChange={(e) => setMunicipality(e.target.value)}
                    data-testid="waitlist-muni-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wl-name">{t("waitlist_contact_name")}</Label>
                  <Input
                    id="wl-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="waitlist-name-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="wl-email">{t("email")}</Label>
                  <Input
                    id="wl-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="waitlist-email-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="wl-note">{t("waitlist_note")}</Label>
                  <Textarea
                    id="wl-note"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="waitlist-note-input"
                  />
                </div>
                <div className="sm:col-span-2 flex justify-end">
                  <Button
                    type="submit"
                    disabled={busy}
                    className="gap-2"
                    data-testid="waitlist-submit-btn"
                  >
                    <Building2 className="h-4 w-4" />
                    {busy ? "…" : t("waitlist_submit")}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
